import {
  NoteDetailResponseSchema,
  NoteListResponseSchema,
  NoteSchema,
  type Note,
  type NoteCreate,
  type NoteListQuery,
  type NoteUpdate,
} from "@l30/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, apiFetchUnchecked } from "./client.js";

const NOTES_KEY = ["notes"] as const;

export function useNotes(query?: Partial<NoteListQuery>, options?: { enabled?: boolean }) {
  const params = new URLSearchParams();
  if (query?.search) params.set("search", query.search);
  if (query?.tag) params.set("tag", query.tag);
  if (query?.kind) params.set("kind", query.kind);
  if (query?.pinned !== undefined) params.set("pinned", String(query.pinned));
  if (query?.includeArchived) params.set("includeArchived", "true");
  if (query?.archivedOnly) params.set("archivedOnly", "true");
  if (query?.limit) params.set("limit", String(query.limit));
  if (query?.offset) params.set("offset", String(query.offset));
  const qs = params.toString();
  return useQuery({
    queryKey: [...NOTES_KEY, query],
    queryFn: () => apiFetch(`/notes${qs ? `?${qs}` : ""}`, {}, NoteListResponseSchema),
    enabled: options?.enabled ?? true,
  });
}

export function useNote(id: string | null) {
  return useQuery({
    queryKey: [...NOTES_KEY, id],
    enabled: !!id,
    queryFn: () => apiFetch(`/notes/${id}`, {}, NoteDetailResponseSchema),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: NoteCreate) =>
      apiFetch<Note>("/notes", { method: "POST", body: JSON.stringify(data) }, NoteSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: NoteUpdate }) =>
      apiFetch<Note>(`/notes/${id}`, { method: "PATCH", body: JSON.stringify(data) }, NoteSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}

export function useArchiveNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Note>(`/notes/${id}/archive`, { method: "POST" }, NoteSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}

export function useUnarchiveNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Note>(`/notes/${id}/unarchive`, { method: "POST" }, NoteSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetchUnchecked<{ deleted: true; id: string }>(`/notes/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}
